import { type Country } from '@capsicum/common';
import React from 'react';
import { type FieldValues, type Path, type PathValue } from 'react-hook-form';

import { useFormElement } from '../useFormElement';

import { type BaseCountryInputProps, BaseCountrySelect } from './BaseCountrySelect';

export type CountrySelectProps<T extends FieldValues, FP extends Path<T>> = Omit<
	BaseCountryInputProps,
	'error' | 'onChange' | 'value'
> & {
	defaultValue?: PathValue<T, FP>;
	name: FP;
	onValueChange?: (value: Country) => void;
	tabIndex?: number;
};

export function CountrySelect<T extends FieldValues, FP extends Path<T> = Path<T>>({
	defaultValue,
	name,
	onValueChange,
	tabIndex,
	...props
}: CountrySelectProps<T, FP>) {
	const {
		field: { onChange: onFormElementChange, value },
		fieldState: { error }
	} = useFormElement<T, FP>({
		defaultValue,
		name
	});

	const onChange = (value: Country) => {
		onFormElementChange(value);
		onValueChange?.(value);
	};

	return <BaseCountrySelect error={error} tabIndex={tabIndex} value={value} onChange={onChange} {...props} />;
}
